/**
 * Voided / deleted offers. The buyer dashboard only shows active contracts;
 * the history lives here, read-only.
 */
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import {
  useGetMyArchivedContractsQuery,
  type ArchivedBuyerContract,
} from '@/apis/extended/contractApi';
import { formatPrice } from '@/utils/helper';

const REASON_LABEL: Record<ArchivedBuyerContract['reason'], string> = {
  voided: 'Voided',
  deleted: 'Deleted',
  property_removed: 'Property removed',
};

export const ProfileArchivedContracts = () => {
  const { data, isLoading } = useGetMyArchivedContractsQuery();
  if (isLoading || !data?.length) return null;

  return (
    <Card>
      <CardHeader
        title="Archived offers"
        subheader="Voided or deleted offers. Your dashboard shows only active ones."
        avatar={<i className="fa-light fa-box-archive" />}
      />
      <CardContent>
        <Stack spacing={1.5}>
          {data.map((c) => (
            <Stack
              key={c._id}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
            >
              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Link
                  component={RouterLink}
                  to={`/view-contract/${c._id}`}
                  underline="hover"
                  sx={{ fontWeight: 600, wordBreak: 'break-word' }}
                >
                  {c.address || 'Property'}
                </Link>
                <Typography variant="caption" color="text.secondary">
                  {c.offerPrice
                    ? `Offer ${formatPrice(c.offerPrice, 0)} · `
                    : ''}
                  {c.archivedAt
                    ? new Date(c.archivedAt).toLocaleDateString()
                    : ''}
                </Typography>
              </Stack>
              <Chip size="small" label={REASON_LABEL[c.reason]} />
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};
